import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Button } from '@/components/Button/Button';
import { MODAL_TIMEOUT } from '@/config/constants';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useSocket } from '@/hooks/useSocket';
import { useWidgetState } from '@/hooks/useWidgetState';
import { shouldDisplaySurveySelector, chatModalState } from '@/state/session';
import { webChatStateState } from '@/state/socket';
import { sleep } from '@/util/utils';
import styles from './CloseChat.module.scss';
/**
 * Shows a chat form when the chatmodal is set to `closing`, `inactivity` or `expired`.
 */
export const CloseChat = ({ type }) => {
    const { t } = useTranslation();
    const { closeSession } = useSocket();
    const { closeWidget } = useWidgetState();
    const setWebChatState = useSetRecoilState(webChatStateState);
    const { trackEvent } = useAnalytics();
    const shouldDisplaySurvey = useRecoilValue(shouldDisplaySurveySelector);
    const setChatModal = useSetRecoilState(chatModalState);
    /**
     * Modal config map with type-specific values
     */
    const modalConfigs = useMemo(() => ({
        ["closing" /* ChatModal.Closing */]: {
            title: t('modal.end_old'),
            primaryButton: t('buttons.end_chat'),
            secondaryButton: t('buttons.cancel'),
            primaryEventTracker: AnalyticsEvent.PopoverEndChatClicked,
            secondaryEventTracker: AnalyticsEvent.PopoverEndChatContinueClicked,
        },
        ["expired" /* ChatModal.Expired */]: {
            title: t('modal.expired'),
            primaryButton: t('buttons.start_new_chat'),
            secondaryButton: t('buttons.close'),
            primaryEventTracker: AnalyticsEvent.PopoverExpiredNewClicked,
            secondaryEventTracker: AnalyticsEvent.PopoverExpiredEndClicked,
        },
        ["inactivity" /* ChatModal.Inactivity */]: {
            title: t('modal.inactivity'),
            primaryButton: t('buttons.start_new_chat'),
            secondaryButton: t('buttons.continue'),
            primaryEventTracker: AnalyticsEvent.PopoverInactivityEndChatClicked,
            secondaryEventTracker: AnalyticsEvent.PopoverInactivityContinueClicked,
        },
        // Add fallbacks for other ChatModal types to prevent TypeScript errors
        ["survey" /* ChatModal.Survey */]: {
            title: '',
            primaryButton: '',
            secondaryButton: '',
        },
        ["survey-full" /* ChatModal.SurveyFull */]: {
            title: '',
            primaryButton: '',
            secondaryButton: '',
        },
        ["closed" /* ChatModal.Resolved */]: {
            title: '',
            primaryButton: '',
            secondaryButton: '',
        },
        ["email" /* ChatModal.Email */]: {
            title: '',
            primaryButton: '',
            secondaryButton: '',
        },
    }), [t]);
    const config = useMemo(() => modalConfigs[type], [modalConfigs, type]);
    /**
     * Primary button click handler
     */
    const handlePrimaryClick = useCallback(async () => {
        if (config.primaryEventTracker) {
            trackEvent(config.primaryEventTracker);
        }
        setChatModal(null);
        if (shouldDisplaySurvey) {
            await sleep(MODAL_TIMEOUT);
            setChatModal("survey" /* ChatModal.Survey */);
        }
        else {
            // Cleaning last session
            closeSession();
            // If the session was expired, and the user selects to start a new one, we need to
            // reinitiate the conversation
            if (type === "expired" /* ChatModal.Expired */) {
                setWebChatState("init" /* WebChatState.Init */);
            }
        }
    }, [
        closeSession,
        config.primaryEventTracker,
        setChatModal,
        setWebChatState,
        shouldDisplaySurvey,
        trackEvent,
        type,
    ]);
    /**
     * Secondary button click handler
     */
    const handleSecondaryClick = useCallback(async () => {
        if (config.secondaryEventTracker) {
            trackEvent(config.secondaryEventTracker);
        }
        setChatModal(null);
        if (type === "expired" /* ChatModal.Expired */) {
            if (shouldDisplaySurvey) {
                await sleep(MODAL_TIMEOUT);
                setChatModal("survey" /* ChatModal.Survey */);
            }
            else {
                closeSession();
                closeWidget();
            }
        }
    }, [
        closeSession,
        closeWidget,
        config.secondaryEventTracker,
        setChatModal,
        shouldDisplaySurvey,
        trackEvent,
        type,
    ]);
    return (_jsxs("div", { className: styles.form, children: [_jsx("div", { className: styles.title, children: config.title }), _jsxs("div", { className: styles.buttonContainer, children: [_jsx(Button, { className: "end-btn" /* TestClassName.EndSessionButton */, onClick: handlePrimaryClick, children: config.primaryButton }), _jsx(Button, { onClick: handleSecondaryClick, variant: "secondary", children: config.secondaryButton })] })] }));
};
